import { create } from "zustand";
import { usersApi, organizationsApi } from "@services/api";
import type { User, Org } from "types";
import { getFlutterFromDate } from "@services/timeHelpers";
import { useProjectStore } from "@store/projectStore";
import { useAuthStore } from "@store/auth";

interface DataStore {
  userData: User | null;
  orgData: Org | null;
  orgMembers: User[];
  isLoadingUserData: boolean;
  isLoadingOrgData: boolean;
  isLoadingOrgMembers: boolean;
  userDataError: string | null;
  orgDataError: string | null;
  orgMembersError: string | null;

  // Actions
  fetchUserData: (userId: string) => Promise<void>;
  fetchOrgData: (orgId: string) => Promise<void>;
  fetchOrgMembers: (orgId: string) => Promise<void>;
  updateUserData: (userId: string, updates: any) => Promise<User>;
  updateOrgData: (orgId: string, updates: any) => Promise<Org>;
  clearUserData: () => void;
  clearOrgData: () => void;
  clearOrgMembers: () => void;
  clearAllData: () => void;
}

export const useDataStore = create<DataStore>((set, get) => ({
  userData: null,
  orgData: null,
  orgMembers: [],
  isLoadingUserData: false,
  isLoadingOrgData: false,
  isLoadingOrgMembers: false,
  userDataError: null,
  orgDataError: null,
  orgMembersError: null,

  fetchUserData: async (userId: string) => {
    set({ isLoadingUserData: true, userDataError: null });
    console.log("📡 Fetching user data from API for userId:", userId);

    try {
      const userData = await usersApi.me(userId);
      console.log("✅ User data fetched:", userData);

      set({
        userData,
        isLoadingUserData: false,
        userDataError: null,
      });

      // Auto-fetch org data, members, and projects if user has org_id
      if (userData.org_id) {
        console.log("📡 Auto-fetching org data for org_id:", userData.org_id);
        await get().fetchOrgData(userData.org_id);
        await get().fetchOrgMembers(userData.org_id);
        useProjectStore.getState().fetchPartners(userData.org_id);
        useProjectStore.getState().fetchBroadcasts(userData.org_id);
        useProjectStore.getState().fetchEmailTemplates(userData.org_id);
        useProjectStore.getState().fetchSentEmails(userData.org_id, undefined, true);
        useProjectStore.getState().fetchInboxEmails(userData.org_id);
        useProjectStore.getState().fetchUnreadCount(userData.org_id);
        useProjectStore.getState().fetchLibraryDocuments(userData.org_id);
        useProjectStore.getState().fetchProjectTemplates(userData.org_id);
        useProjectStore.getState().fetchFormTemplates(userData.org_id);
        useProjectStore.getState().fetchPlanningTemplates(userData.org_id);
        useProjectStore.getState().fetchSyncOperations(userData.org_id);
      }
    } catch (error: any) {
      console.error("❌ Error fetching user data:", error);

      const status = error?.response?.status;
      const hadUserData = !!get().userData;

      // Only wipe userData + force logout on real auth failures (401/403/404).
      // Transient backend errors (500, network) must NOT clear the cached
      // profile — otherwise `isGUEST(null)` falls back to `false` and guest
      // users briefly see admin-only pages before the re-auth completes.
      // The axios client already handles 401 refresh+retry+logout itself.
      const isAuthFailure = status === 401 || status === 403 || status === 404;

      if (isAuthFailure || !hadUserData) {
        set({
          userData: null,
          isLoadingUserData: false,
          userDataError: "Échec du chargement des données utilisateur",
        });
        if (isAuthFailure) {
          console.log("🚪 Logging out user due to auth failure on user data fetch");
          await useAuthStore.getState().logout();
        }
      } else {
        // Keep the stale-but-valid userData so the UI doesn't regress to
        // the default (non-guest) view. Surface the error only.
        set({
          isLoadingUserData: false,
          userDataError: "Échec du chargement des données utilisateur",
        });
      }
    }
  },

  fetchOrgData: async (orgId: string) => {
    set({ isLoadingOrgData: true, orgDataError: null });
    console.log("📡 Fetching org data from API for org_id:", orgId);

    try {
      let orgDataResponse = await organizationsApi.get(orgId);
      console.log("✅ Org data fetched:", orgDataResponse);
      let orgData: Org = {
        ...orgDataResponse,
        creation_date: getFlutterFromDate(
          new Date(orgDataResponse.creation_date),
        ),
      };

      set({
        orgData,
        isLoadingOrgData: false,
        orgDataError: null,
      });
    } catch (error: any) {
      console.error("❌ Error fetching org data:", error);
      set({
        orgData: null,
        isLoadingOrgData: false,
        orgDataError: "Échec du chargement des données de l'organisation",
      });
    }
  },

  fetchOrgMembers: async (orgId: string) => {
    set({ isLoadingOrgMembers: true, orgMembersError: null });
    console.log("📡 Fetching org members from API for org_id:", orgId);

    try {
      const orgMembers = await usersApi.list({ org_id: orgId });

      set({
        orgMembers,
        isLoadingOrgMembers: false,
        orgMembersError: null,
      });
    } catch (error: any) {
      console.error("❌ Error fetching org members:", error);
      set({
        orgMembers: [],
        isLoadingOrgMembers: false,
        orgMembersError: "Échec du chargement des membres de l'équipe",
      });
    }
  },

  updateUserData: async (userId: string, updates: any) => {
    console.log("📡 Updating user data for userId:", userId);

    try {
      const updatedUser = await usersApi.update(userId, updates);
      console.log("✅ User data updated:", updatedUser);

      // Update local state if updating current user
      const currentUserData = get().userData;
      if (currentUserData?.id === userId) {
        set({ userData: updatedUser });
      }

      // Update in orgMembers list if present
      const orgMembers = get().orgMembers;
      const memberIndex = orgMembers.findIndex((m) => m.id === userId);
      if (memberIndex !== -1) {
        const newOrgMembers = [...orgMembers];
        newOrgMembers[memberIndex] = updatedUser;
        set({ orgMembers: newOrgMembers });
      }

      return updatedUser;
    } catch (error: any) {
      console.error("❌ Error updating user data:", error);
      throw error;
    }
  },

  updateOrgData: async (orgId: string, updates: any) => {
    console.log("📡 Updating org data for orgId:", orgId);

    try {
      let updatedOrgResponse = await organizationsApi.update(orgId, updates);
      console.log("✅ Org data updated:", updatedOrgResponse);

      let updatedOrg: Org = {
        ...updatedOrgResponse,
        creation_date: getFlutterFromDate(
          new Date(updatedOrgResponse.creation_date),
        ),
      };

      // Update local state
      set({ orgData: updatedOrg });

      return updatedOrg;
    } catch (error: any) {
      console.error("❌ Error updating org data:", error);
      throw error;
    }
  },

  clearUserData: () => {
    set({
      userData: null,
      isLoadingUserData: false,
      userDataError: null,
    });
  },

  clearOrgData: () => {
    set({
      orgData: null,
      isLoadingOrgData: false,
      orgDataError: null,
    });
  },

  clearOrgMembers: () => {
    set({
      orgMembers: [],
      isLoadingOrgMembers: false,
      orgMembersError: null,
    });
  },

  clearAllData: () => {
    get().clearUserData();
    get().clearOrgData();
    get().clearOrgMembers();
  },
}));
